<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="card"
    />

    <template v-if="!isLoading">
      <v-row
        v-if="recentCustomers.length > 0 || upcomingBirthdayCustomers.length > 0"
        class="mb-0"
      >
        <!-- 最近来店した顧客 -->
        <v-col
          v-if="recentCustomers.length > 0"
          :cols="upcomingBirthdayCustomers.length > 0 ? 6 : 12"
        >
          <CustomerRecentVisits
            :customers="recentCustomers"
            @click="goToCustomerDetail"
          />
        </v-col>

        <!-- 30日以内に誕生日の顧客 -->
        <v-col
          v-if="upcomingBirthdayCustomers.length > 0"
          :cols="recentCustomers.length > 0 ? 6 : 12"
        >
          <CustomerUpcomingBirthdays
            :customers="upcomingBirthdayCustomers"
            @click="goToCustomerDetail"
          />
        </v-col>
      </v-row>

      <v-row>
        <!-- フィルターパネル (左側) -->
        <v-col
          cols="3"
        >
          <CustomerFilterPanel
            :customer-tags="customerTags"
            @apply="handleFilterApply"
            @reset="handleFilterReset"
            @tag-updated="handleTagUpdated"
          />
        </v-col>

        <!-- 顧客一覧 (右側) -->
        <v-col cols="9">
          <!-- 検索バー（インクリメンタルサーチ） -->
          <div class="d-flex align-center mb-4">
            <v-text-field
              v-model="searchQuery"
              :prepend-inner-icon="icons.mdiMagnify"
              label="名前・会員番号で検索"
              single-line
              hide-details
              clearable
              dense
              outlined
              style="max-width: 400px;"
              class="mr-2"
              @input="handleSearchDebounced"
              @click:clear="handleClearSearch"
            />
          </div>

          <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
              <span>顧客一覧</span>
              <div>
                <v-btn
                  color="secondary"
                  outlined
                  class="mr-2"
                  @click="openAddPropertyDialog"
                >
                  <v-icon left>
                    {{ icons.mdiPlaylistPlus }}
                  </v-icon>
                  プロパティを追加
                </v-btn>
                <v-btn
                  color="primary"
                  @click="openNewCustomerDialog"
                >
                  <v-icon left>
                    {{ icons.mdiPlus }}
                  </v-icon>
                  顧客を追加
                </v-btn>
              </div>
            </v-card-title>

            <v-card-text>
              <CustomerList
                :customers="customers"
                :custom-fields="customFields"
                :total-count="pagination.totalCount"
                :page="pagination.page"
                :per-page="pagination.perPage"
                :loading="isSearching"
                @click="handleCustomerClick"
                @update:pagination="handlePaginationChange"
                @create-table="handleCreateTableFromCustomer"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- 新規顧客追加ダイアログ -->
    <v-dialog
      v-if="newCustomerDialog"
      v-model="newCustomerDialog"
      width="500"
      persistent
    >
      <CustomerForm
        :type="'newUser'"
        @submit="handleCustomerCreate"
        @cancel="newCustomerDialog = false"
      />
    </v-dialog>

    <!-- 顧客からの卓作成ダイアログ -->
    <CreateTableFromCustomerDialog
      v-model="createTableDialog"
      :customer="createTableCustomer"
      @created="handleTableCreated"
    />

    <!-- プロパティ追加/編集ダイアログ -->
    <CustomerPropertyFormDialog
      v-model="propertyFormDialog"
      :property="editingProperty"
      @submit="handleAddProperty"
      @update="handleUpdateProperty"
    />

    <!-- プロパティ削除確認ダイアログ -->
    <CustomerPropertyDeleteConfirm
      v-model="deleteConfirmDialog"
      :property="deletingProperty"
      @confirm="handleDeleteProperty"
    />
  </div>
</template>

<script>
import {
  getCurrentInstance,
  onMounted,
  ref,
} from '@vue/composition-api'
import { mdiPlus, mdiMagnify, mdiPlaylistPlus } from '@mdi/js'
import { debounce } from 'lodash'
import useCompInit from '@/views/composable/useCompInit'
import useCustomers from '@/views/v2-temp/common/customer-management/composables/useCustomers'
import useCustomerCustomFields from '@/views/v2-temp/common/customer-management/composables/useCustomerCustomFields'
import CustomerForm from '@/views/v2-temp/common/customer-management/components/CustomerForm.vue'
import CustomerList from '@/views/v2-temp/common/customer-management/components/CustomerList.vue'
import CustomerFilterPanel from '@/views/v2-temp/common/customer-management/components/CustomerFilterPanel.vue'
import CustomerRecentVisits from '@/views/v2-temp/common/customer-management/components/CustomerRecentVisits.vue'
import CustomerUpcomingBirthdays from '@/views/v2-temp/common/customer-management/components/CustomerUpcomingBirthdays.vue'
import CreateTableFromCustomerDialog from '@/views/v2-temp/common/customer-management/components/CreateTableFromCustomerDialog.vue'
import CustomerPropertyFormDialog from '@/views/v2-temp/common/customer-management/components/CustomerPropertyFormDialog.vue'
import CustomerPropertyDeleteConfirm from '@/views/v2-temp/common/customer-management/components/CustomerPropertyDeleteConfirm.vue'
import CustomerTagApi from '@/api/waiter/CustomerTag'

export default {
  name: 'WaiterCustomerManagement',
  components: {
    CustomerForm,
    CustomerList,
    CustomerFilterPanel,
    CustomerRecentVisits,
    CustomerUpcomingBirthdays,
    CreateTableFromCustomerDialog,
    CustomerPropertyFormDialog,
    CustomerPropertyDeleteConfirm,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const { initWith, isLoading } = useCompInit()

    const {
      customers,
      newCustomerDialog,
      searchQuery,
      isSearching,
      pagination,
      recentCustomers,
      upcomingBirthdayCustomers,
      activeFilters,
      getCustomers,
      searchCustomers,
      createCustomer,
      openNewCustomerDialog,
      getCustomersWithStatsFiltered,
      applyFilters,
      resetFilters,
    } = useCustomers(vm)

    const {
      customFields,
      propertyFormDialog,
      editingProperty,
      deleteConfirmDialog,
      deletingProperty,
      getCustomFields,
      addCustomField,
      updateCustomField,
      deleteCustomField,
      openAddPropertyDialog,
    } = useCustomerCustomFields(vm)

    // 顧客タグ一覧
    const customerTags = ref([])
    const getCustomerTags = async () => {
      try {
        const res = await CustomerTagApi.getCustomerTags()
        customerTags.value = (res.data?.customerTags?.data || []).map(t => ({
          id: t.id,
          name: t.attributes?.name || t.name,
        }))
      } catch (e) {
        // タグ取得失敗時は空のまま
      }
    }

    // 卓作成ダイアログ
    const createTableDialog = ref(false)
    const createTableCustomer = ref(null)
    const isCreateTableMode = ref(false)

    const handleCreateTableFromCustomer = customer => {
      createTableCustomer.value = customer
      createTableDialog.value = true
    }

    const handleTableCreated = () => {
      createTableDialog.value = false
      vm.$toast.success('卓を作成しました')
      vm.$router.push({
        name: 'waiter-halls',
        params: { club_slug: vm.$route.params.club_slug },
      })
    }

    const handleCustomerCreate = async event => {
      const success = await createCustomer(event)
      if (success) {
        newCustomerDialog.value = false
      }
    }

    // インクリメンタルサーチ（300msデバウンス）
    const handleSearchDebounced = debounce(() => {
      searchCustomers(searchQuery.value, { page: 1, perPage: pagination.value.perPage })
    }, 300)

    const handleClearSearch = () => {
      searchQuery.value = ''
      getCustomers({ page: 1, perPage: pagination.value.perPage })
    }

    const handlePaginationChange = ({ page, perPage }) => {
      if (searchQuery.value) {
        searchCustomers(searchQuery.value, { page, perPage })
      } else {
        getCustomers({ page, perPage })
      }
    }

    const handleFilterApply = filters => {
      applyFilters(filters)
    }

    const handleFilterReset = () => {
      resetFilters()
    }

    const goToCustomerDetail = customer => {
      vm.$router.push({
        name: 'waiter-settings-customer-management-show',
        params: {
          club_slug: vm.$route.params.club_slug,
          customerId: customer.id,
        },
      })
    }

    // 顧客クリック: create_table モード時はダイアログ、通常は詳細
    const handleCustomerClick = customer => {
      if (isCreateTableMode.value) {
        handleCreateTableFromCustomer(customer)
      } else {
        goToCustomerDetail(customer)
      }
    }

    const handleRecentCustomerClick = customer => {
      goToCustomerDetail(customer)
    }

    const handleAddProperty = event => {
      addCustomField(event)
    }

    const handleUpdateProperty = event => {
      updateCustomField(event.id, event)
    }

    const handleDeleteProperty = event => {
      deleteCustomField(deletingProperty.value.id, event)
    }

    const handleTagUpdated = ({ id, name }) => {
      const idx = customerTags.value.findIndex(t => t.id === id)
      if (idx >= 0) {
        customerTags.value.splice(idx, 1, { ...customerTags.value[idx], name })
      }
    }

    onMounted(() => {
      // create_table モードの検出
      isCreateTableMode.value = vm.$route.query.mode === 'create_table'

      initWith([
        getCustomers(),
        getCustomFields(),
        getCustomerTags(),
        getCustomersWithStatsFiltered(),
      ])
    })

    return {
      isLoading,
      isSearching,
      customers,
      newCustomerDialog,
      searchQuery,
      pagination,
      customFields,
      customerTags,
      recentCustomers,
      upcomingBirthdayCustomers,
      activeFilters,
      createTableDialog,
      createTableCustomer,
      propertyFormDialog,
      editingProperty,
      deleteConfirmDialog,
      deletingProperty,
      openNewCustomerDialog,
      openAddPropertyDialog,
      handleCustomerCreate,
      handleAddProperty,
      handleUpdateProperty,
      handleDeleteProperty,
      handleSearchDebounced,
      handleClearSearch,
      handlePaginationChange,
      handleFilterApply,
      handleFilterReset,
      handleCustomerClick,
      handleRecentCustomerClick,
      handleCreateTableFromCustomer,
      handleTableCreated,
      handleTagUpdated,
      goToCustomerDetail,
      icons: {
        mdiPlus,
        mdiMagnify,
        mdiPlaylistPlus,
      },
    }
  },
}
</script>
